import { BrowserRouter } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Layout from "./components/Layout";
import { Provider } from "react-redux";
import { store } from "store";
import Authorization from "./components/Authroization";
import { SnackbarProvider } from "notistack";
import { I18nextProvider, useTranslation } from "react-i18next";
import { Suspense, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { AuthState } from "store/features/auth";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";

const sharedComponents = {
  MuiCssBaseline: {
    styleOverrides: {
      "*": {
        transition:
          "background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
      },
      "::-webkit-scrollbar": { width: 6, height: 6 },
      "::-webkit-scrollbar-track": { background: "transparent" },
      "::-webkit-scrollbar-thumb": {
        background: "rgba(0,0,0,0.15)",
        borderRadius: 3,
        "&:hover": { background: "rgba(0,0,0,0.25)" },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        textTransform: "none" as const,
        fontWeight: 600,
        boxShadow: "none",
        letterSpacing: "0.01em",
        "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
      },
      sizeSmall: {
        padding: "5px 14px",
        fontSize: "0.8125rem",
      },
      sizeMedium: {
        padding: "8px 20px",
      },
      sizeLarge: {
        padding: "10px 26px",
        fontSize: "0.9375rem",
      },
      outlined: {
        borderWidth: 1.5,
        "&:hover": { borderWidth: 1.5 },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0,0,0,0.12)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0,0,0,0.24)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderWidth: 1.5,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 14,
        borderColor: "rgba(0,0,0,0.08)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
        boxShadow: "0 24px 48px -12px rgba(0,0,0,0.18)",
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        borderRadius: 8,
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        borderRadius: 14,
        textTransform: "none" as const,
        fontWeight: 600,
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.2)" },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontWeight: 700,
        fontSize: "0.8125rem",
        letterSpacing: "0.02em",
      },
      root: {
        borderColor: "rgba(0,0,0,0.06)",
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&.MuiTableRow-hover:hover": {
          backgroundColor: "rgba(0,0,0,0.02)",
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: "none" as const,
        fontWeight: 600,
        minHeight: 48,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 8,
        fontSize: "0.75rem",
        fontWeight: 500,
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontSize: "0.875rem",
        fontWeight: 600,
      },
    },
  },
};

const ROLE_PALETTES: Record<
  string,
  { main: string; light: string; dark: string }
> = {
  NURSERY_ADMIN: { main: "#6C3FCF", light: "#9B6FE8", dark: "#4A1FA8" },
  ROOM_LEADER: { main: "#00897b", light: "#4db6ac", dark: "#00695c" },
  NURSERY_PRACTITIONER: { main: "#00897b", light: "#4db6ac", dark: "#00695c" },
  PARENT: { main: "#3b82f6", light: "#60a5fa", dark: "#2563eb" },
};

const sharedTypography = {
  button: { textTransform: "none" as const },
  h6: { fontWeight: 700, letterSpacing: "-0.01em" },
  subtitle1: { fontWeight: 600 },
  subtitle2: { fontWeight: 700, letterSpacing: "0.01em" },
  body2: { lineHeight: 1.6 },
  caption: { letterSpacing: "0.02em" },
};

const buildTheme = (direction: "rtl" | "ltr", role?: string) => {
  const primary =
    ROLE_PALETTES[role || "NURSERY_ADMIN"] || ROLE_PALETTES.NURSERY_ADMIN;
  return createTheme({
    direction,
    palette: {
      primary,
      background: { default: "#FAFAFA", paper: "#FFFFFF" },
      divider: "rgba(0,0,0,0.08)",
    },
    typography: {
      fontFamily:
        direction === "rtl"
          ? "'Noto Sans Arabic', sans-serif"
          : "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      ...sharedTypography,
    },
    shape: { borderRadius: 10 },
    components: sharedComponents,
  });
};

const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

const cacheLtr = createCache({
  key: "muiltr",
  stylisPlugins: [],
});

function ThemedApp() {
  const { i18n } = useTranslation();
  const { role } = useSelector<RootState, AuthState>((store) => store.auth);
  const dir = i18n.dir(i18n.language) as "rtl" | "ltr";
  const theme = useMemo(() => buildTheme(dir, role || undefined), [dir, role]);

  useEffect(() => {
    document.documentElement.dir = dir;
  }, [dir]);

  return (
    <CacheProvider value={dir === "rtl" ? cacheRtl : cacheLtr}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <SnackbarProvider maxSnack={3} autoHideDuration={5000}>
            <Authorization>
              <Layout />
            </Authorization>
          </SnackbarProvider>
        </BrowserRouter>
      </ThemeProvider>
    </CacheProvider>
  );
}

function App() {
  const { i18n } = useTranslation();

  return (
    <div className={i18n.language === "ar" ? "App font-ar" : "App"}>
      <I18nextProvider i18n={i18n}>
        <Suspense fallback="loading">
          <Provider store={store}>
            <ThemedApp />
          </Provider>
        </Suspense>
      </I18nextProvider>
    </div>
  );
}

export default App;
